import React, { useEffect, useRef, useState } from 'react';
import ExperimentNav from '../../components/ExperimentNav';
import { experiments } from '../../App';
import './FossilDig.css';

// ── Web Fossil Dig ─────────────────────────────────────────────────────────
// A z-stack of noisy strata painted onto a cached "ground" canvas. Double-click
// carves a circular hole one layer deeper at that spot; dig the same crater past
// 5 layers and a fossil surfaces. The *rhythm* of your double-clicks maps to
// pickaxe force — fast taps = hard strike (big debris + screen shake + CRACK!),
// slow taps = a brush (dust drifts down). No audio: the "sound" of digging is
// faked entirely with motion-blurred debris, shake, and onomatopoeia pop-ups.
// Pure Canvas 2D + requestAnimationFrame + particles. No external libraries.

// Surface → bedrock: beige / brown / gray rotated, darkening with depth.
const STRATA = ['#d3ba8d', '#b0824f', '#9d9184', '#c4a875', '#8c5d38', '#8f857b', '#6d4a2c', '#463f38'];

const D = STRATA.length;            // 8 layers; bedrock (last) never removed
const FOSSIL_DEPTH = 5;             // dig this many layers → fossil surfaces
const TOP_RADIUS = 82;
const RING = 8;                     // each deeper layer's hole shrinks by this
const FOSSILS = ['wifi', 'dino', 'floppy', 'bone'] as const;
type FossilType = typeof FOSSILS[number];

interface Site { x: number; y: number; depth: number; jitter: number[]; fossil: FossilType | null; }
interface Particle { x: number; y: number; px: number; py: number; vx: number; vy: number; life: number; max: number; size: number; color: string; grav: number; }
interface Pop { x: number; y: number; text: string; age: number; dur: number; color: string; rot: number; vy: number; }

type Strength = 'strong' | 'medium' | 'brush';

const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));
const rand = (a: number, b: number) => a + Math.random() * (b - a);

// hex → lighten/darken by amt (−1..1)
function shade(hex: string, amt: number): string {
  const h = hex.replace('#', '');
  let r = parseInt(h.slice(0, 2), 16);
  let g = parseInt(h.slice(2, 4), 16);
  let b = parseInt(h.slice(4, 6), 16);
  const t = amt < 0 ? 0 : 255;
  const p = Math.abs(amt);
  r = Math.round((t - r) * p + r);
  g = Math.round((t - g) * p + g);
  b = Math.round((t - b) * p + b);
  return `rgb(${r},${g},${b})`;
}

const radiusForLayer = (d: number) => Math.max(16, TOP_RADIUS - d * RING);

// Chrome-dino-ish silhouette, drawn as a fossil imprint.
const DINO = [
  '00000001111',
  '00000011101',
  '00000011111',
  '00000011000',
  '01000011110',
  '01100111100',
  '01111111000',
  '00111111000',
  '00011110000',
  '00010010000',
  '00110011000',
];

const FossilDig: React.FC = () => {
  const dispRef = useRef<HTMLCanvasElement>(null);

  const groundRef = useRef<HTMLCanvasElement | null>(null);
  const scratchRef = useRef<HTMLCanvasElement | null>(null);
  const layerTexRef = useRef<HTMLCanvasElement[]>([]);
  const sizeRef = useRef({ w: 0, h: 0 });

  const sitesRef = useRef<Site[]>([]);
  const particlesRef = useRef<Particle[]>([]);
  const popsRef = useRef<Pop[]>([]);

  const lastDigRef = useRef(0);
  const shakeRef = useRef(0);
  const rafRef = useRef(0);
  const lastTRef = useRef(0);
  const reducedRef = useRef(false);

  const [depth, setDepth] = useState(0);
  const [strength, setStrength] = useState<Strength | null>(null);
  const [foundFossils, setFoundFossils] = useState<FossilType[]>([]);

  // ── strata textures ──────────────────────────────────────────────────────
  const paintLayer = (canvas: HTMLCanvasElement, base: string, idx: number) => {
    const { w, h } = sizeRef.current;
    const g = canvas.getContext('2d')!;
    g.clearRect(0, 0, w, h);
    // vertical shading so each slab reads as solid earth
    const grad = g.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0, shade(base, 0.06));
    grad.addColorStop(1, shade(base, -0.12));
    g.fillStyle = grad;
    g.fillRect(0, 0, w, h);
    // grain specks
    const n = Math.floor((w * h) / 90);
    for (let i = 0; i < n; i++) {
      const x = Math.random() * w;
      const y = Math.random() * h;
      const dark = Math.random() < 0.5;
      g.fillStyle = dark ? 'rgba(40,26,14,0.10)' : 'rgba(255,246,230,0.09)';
      const s = Math.random() < 0.9 ? 1 : 2;
      g.fillRect(x, y, s, s);
    }
    // sediment streaks
    for (let k = 0; k < 7; k++) {
      const y = Math.random() * h;
      g.strokeStyle = k % 2 ? 'rgba(50,34,20,0.08)' : 'rgba(255,244,224,0.07)';
      g.lineWidth = Math.random() < 0.5 ? 1 : 2;
      g.beginPath();
      g.moveTo(0, y);
      let cy = y;
      for (let x = 0; x <= w; x += 60) {
        cy += rand(-6, 6);
        g.lineTo(x, cy);
      }
      g.stroke();
    }
    // occasional pebbles (more in deeper layers)
    const pebbles = 6 + idx * 3;
    for (let k = 0; k < pebbles; k++) {
      const x = Math.random() * w;
      const y = Math.random() * h;
      const r = rand(2, 6);
      g.fillStyle = shade(base, -0.22);
      g.beginPath();
      g.ellipse(x, y, r, r * 0.8, Math.random() * Math.PI, 0, Math.PI * 2);
      g.fill();
      g.fillStyle = shade(base, 0.14);
      g.beginPath();
      g.ellipse(x - r * 0.25, y - r * 0.25, r * 0.4, r * 0.32, 0, 0, Math.PI * 2);
      g.fill();
    }
  };

  const buildTextures = () => {
    const { w, h } = sizeRef.current;
    const arr: HTMLCanvasElement[] = [];
    for (let d = 0; d < D; d++) {
      const c = document.createElement('canvas');
      c.width = w;
      c.height = h;
      paintLayer(c, STRATA[d], d);
      arr.push(c);
    }
    layerTexRef.current = arr;
  };

  // jittered crater outline (deterministic per site) → path only
  const craterPath = (g: CanvasRenderingContext2D, s: Site, r: number) => {
    g.beginPath();
    const seg = s.jitter.length;
    for (let i = 0; i <= seg; i++) {
      const a = (i / seg) * Math.PI * 2;
      const jr = Math.max(4, r) + s.jitter[i % seg];
      const x = s.x + Math.cos(a) * jr;
      const y = s.y + Math.sin(a) * jr;
      if (i === 0) g.moveTo(x, y);
      else g.lineTo(x, y);
    }
    g.closePath();
  };
  const cutCrater = (g: CanvasRenderingContext2D, s: Site, r: number) => {
    craterPath(g, s, r);
    g.fill();
  };

  const drawFossil = (g: CanvasRenderingContext2D, s: Site) => {
    const r = radiusForLayer(s.depth - 1) * 0.72;
    const cx = s.x;
    const cy = s.y;
    const bone = '#e9dcc0';
    const line = 'rgba(60,44,26,0.85)';
    g.save();
    g.translate(cx, cy);
    if (s.fossil === 'bone') {
      g.font = `${r * 2}px system-ui, sans-serif`;
      g.textAlign = 'center';
      g.textBaseline = 'middle';
      g.globalAlpha = 0.92;
      g.fillText('🦴', 0, r * 0.08);
    } else if (s.fossil === 'wifi') {
      g.strokeStyle = bone;
      g.lineCap = 'round';
      for (let i = 0; i < 3; i++) {
        g.lineWidth = r * 0.16;
        g.beginPath();
        g.arc(0, r * 0.55, r * (0.35 + i * 0.32), Math.PI * 1.25, Math.PI * 1.75);
        g.stroke();
      }
      g.fillStyle = bone;
      g.beginPath();
      g.arc(0, r * 0.55, r * 0.13, 0, Math.PI * 2);
      g.fill();
    } else if (s.fossil === 'floppy') {
      const sz = r * 1.5;
      g.translate(-sz / 2, -sz / 2);
      g.fillStyle = bone;
      g.beginPath();
      g.moveTo(0, 0);
      g.lineTo(sz * 0.82, 0);
      g.lineTo(sz, sz * 0.18);
      g.lineTo(sz, sz);
      g.lineTo(0, sz);
      g.closePath();
      g.fill();
      g.fillStyle = line;
      g.fillRect(sz * 0.22, 0, sz * 0.44, sz * 0.3); // shutter
      g.fillStyle = 'rgba(60,44,26,0.55)';
      g.fillRect(sz * 0.18, sz * 0.42, sz * 0.64, sz * 0.42); // label
    } else {
      // pixel dino
      const cols = DINO[0].length;
      const rows = DINO.length;
      const px = (r * 2) / cols;
      g.fillStyle = bone;
      for (let ry = 0; ry < rows; ry++) {
        for (let rx = 0; rx < cols; rx++) {
          if (DINO[ry][rx] === '1') {
            g.fillRect(-r + rx * px, -r * (rows / cols) + ry * px, px + 0.5, px + 0.5);
          }
        }
      }
    }
    g.restore();
  };

  // full re-render of the cached ground (only on dig / resize)
  const renderGround = () => {
    const ground = groundRef.current!;
    const scratch = scratchRef.current!;
    const { w, h } = sizeRef.current;
    const g = ground.getContext('2d')!;
    const sc = scratch.getContext('2d')!;
    g.clearRect(0, 0, w, h);
    g.fillStyle = '#2a251f'; // void beneath, in case
    g.fillRect(0, 0, w, h);

    for (let d = D - 1; d >= 0; d--) {
      sc.globalCompositeOperation = 'source-over';
      sc.clearRect(0, 0, w, h);
      sc.drawImage(layerTexRef.current[d], 0, 0);
      sc.globalCompositeOperation = 'destination-out';
      for (const s of sitesRef.current) {
        if (s.depth > d) cutCrater(sc, s, radiusForLayer(d));
      }
      sc.globalCompositeOperation = 'source-over';
      g.drawImage(scratch, 0, 0);
    }

    // Merged inner-shadow along the UNION rim of every pit mouth, so adjacent
    // craters read as one continuous excavation instead of stacked circles:
    // fill the union solid, then erode the interior — the surviving band hugs
    // only the outer silhouette (junction seams get erased away).
    const active = sitesRef.current.filter((s) => s.depth > 0);
    if (active.length) {
      sc.globalCompositeOperation = 'source-over';
      sc.clearRect(0, 0, w, h);
      sc.fillStyle = '#000';
      for (const s of active) cutCrater(sc, s, radiusForLayer(0));
      sc.globalCompositeOperation = 'destination-out';
      for (const s of active) cutCrater(sc, s, radiusForLayer(0) - 13);
      sc.globalCompositeOperation = 'source-over';
      g.globalAlpha = 0.3;
      g.drawImage(scratch, 0, 0);
      g.globalAlpha = 1;
    }

    // fossils sit at the floor of their crater
    for (const s of sitesRef.current) {
      if (s.fossil && s.depth >= FOSSIL_DEPTH) drawFossil(g, s);
    }
  };

  // ── dig! ──────────────────────────────────────────────────────────────────
  const dig = (x: number, y: number) => {
    const now = performance.now();
    const prev = lastDigRef.current;
    const interval = prev === 0 ? 400 : now - prev;
    lastDigRef.current = now;

    let str: Strength;
    if (interval < 250) str = 'strong';
    else if (interval > 600) str = 'brush';
    else str = 'medium';

    // deepen an existing crater if we clicked inside its mouth, else new one
    let site = sitesRef.current.find(
      (s) => Math.hypot(s.x - x, s.y - y) < radiusForLayer(0) * 0.9
    );
    if (!site) {
      const seg = 26;
      const jitter = Array.from({ length: seg }, () => rand(-4, 4));
      site = { x, y, depth: 0, jitter, fossil: null };
      sitesRef.current.push(site);
    }
    if (site.depth < D - 1) site.depth += 1; // never strip bedrock

    // fossil surfaces
    if (site.depth >= FOSSIL_DEPTH && !site.fossil) {
      site.fossil = FOSSILS[Math.floor(Math.random() * FOSSILS.length)];
      setFoundFossils((f) => (f.includes(site!.fossil!) ? f : [...f, site!.fossil!]));
    }

    const digColor = STRATA[clamp(site.depth - 1, 0, D - 1)];
    spawnDebris(x, y, str, digColor);

    if (!reducedRef.current) {
      shakeRef.current = str === 'strong' ? rand(3, 5) : str === 'medium' ? 2 : 0;
    }
    spawnPop(x, y, str);

    renderGround();
    const maxDepth = sitesRef.current.reduce((m, s) => Math.max(m, s.depth), 0);
    setDepth(maxDepth);
    setStrength(str);
  };

  const spawnDebris = (x: number, y: number, str: Strength, color: string) => {
    const ps = particlesRef.current;
    if (str === 'brush') {
      const n = Math.floor(rand(7, 12));
      for (let i = 0; i < n; i++) {
        ps.push({
          x: x + rand(-14, 14), y: y + rand(-6, 6), px: x, py: y,
          vx: rand(-0.03, 0.03), vy: rand(-0.02, 0.04),
          life: 0, max: rand(700, 1300), size: rand(1, 2.4),
          color: shade(color, 0.12), grav: 0.00018,
        });
      }
    } else {
      const hard = str === 'strong';
      const n = Math.floor(hard ? rand(18, 30) : rand(11, 18));
      for (let i = 0; i < n; i++) {
        const ang = rand(-Math.PI * 0.9, -Math.PI * 0.1);
        const spd = (hard ? rand(0.22, 0.62) : rand(0.12, 0.4));
        ps.push({
          x, y, px: x, py: y,
          vx: Math.cos(ang) * spd + rand(-0.05, 0.05),
          vy: Math.sin(ang) * spd,
          life: 0, max: rand(500, 1000),
          size: hard ? rand(2.5, 6) : rand(1.8, 4),
          color: Math.random() < 0.5 ? shade(color, -0.1) : shade(color, 0.1),
          grav: 0.0016,
        });
      }
    }
  };

  const spawnPop = (x: number, y: number, str: Strength) => {
    const map: Record<Strength, { text: string; color: string }> = {
      strong: { text: 'CRACK!', color: '#ff5c33' },
      medium: { text: 'THUD!', color: '#ffb020' },
      brush: { text: 'sss~', color: '#c9b48c' },
    };
    const { text, color } = map[str];
    popsRef.current.push({
      x, y: y - 24, text, age: 0, dur: str === 'brush' ? 900 : 700,
      color, rot: rand(-0.18, 0.18), vy: str === 'brush' ? -0.02 : -0.05,
    });
  };

  // ── main loop (fx overlay + shake) ─────────────────────────────────────────
  useEffect(() => {
    const disp = dispRef.current!;
    const dctx = disp.getContext('2d')!;
    reducedRef.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const resize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      sizeRef.current = { w, h };
      for (const c of [disp, groundRef.current!, scratchRef.current!]) {
        c.width = w;
        c.height = h;
      }
      buildTextures();
      renderGround();
    };

    groundRef.current = document.createElement('canvas');
    scratchRef.current = document.createElement('canvas');
    resize();
    window.addEventListener('resize', resize);

    const frame = (t: number) => {
      const dt = Math.min(48, t - (lastTRef.current || t));
      lastTRef.current = t;
      const { w, h } = sizeRef.current;

      // screen shake decay
      shakeRef.current *= 0.86;
      if (shakeRef.current < 0.15) shakeRef.current = 0;
      const sx = shakeRef.current ? rand(-1, 1) * shakeRef.current : 0;
      const sy = shakeRef.current ? rand(-1, 1) * shakeRef.current : 0;

      dctx.clearRect(0, 0, w, h);
      dctx.drawImage(groundRef.current!, sx, sy);

      // particles (motion-blurred streaks + soft ghost)
      const ps = particlesRef.current;
      for (let i = ps.length - 1; i >= 0; i--) {
        const p = ps[i];
        p.px = p.x;
        p.py = p.y;
        p.vy += p.grav * dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.life += dt;
        if (p.life >= p.max || p.y > h + 40) {
          ps.splice(i, 1);
          continue;
        }
        const a = 1 - p.life / p.max;
        dctx.strokeStyle = p.color;
        dctx.globalAlpha = a * 0.9;
        dctx.lineWidth = p.size;
        dctx.lineCap = 'round';
        dctx.beginPath();
        dctx.moveTo(p.px + sx, p.py + sy);
        dctx.lineTo(p.x + sx, p.y + sy);
        dctx.stroke();
        // cartoon ghost
        dctx.globalAlpha = a * 0.25;
        dctx.fillStyle = p.color;
        dctx.beginPath();
        dctx.arc(p.x + sx, p.y + sy, p.size * 0.7, 0, Math.PI * 2);
        dctx.fill();
      }
      dctx.globalAlpha = 1;

      // onomatopoeia pops
      const pops = popsRef.current;
      dctx.textAlign = 'center';
      dctx.textBaseline = 'middle';
      for (let i = pops.length - 1; i >= 0; i--) {
        const pp = pops[i];
        pp.age += dt;
        pp.y += pp.vy * dt;
        if (pp.age >= pp.dur) {
          pops.splice(i, 1);
          continue;
        }
        const k = pp.age / pp.dur;
        const pop = k < 0.2 ? k / 0.2 : 1; // pop-in
        const alpha = k < 0.6 ? 1 : 1 - (k - 0.6) / 0.4;
        const scale = (0.6 + pop * 0.6) * (1 + k * 0.15);
        dctx.save();
        dctx.translate(pp.x + sx, pp.y + sy);
        dctx.rotate(pp.rot);
        dctx.scale(scale, scale);
        dctx.font = '900 34px "JetBrains Mono", ui-monospace, monospace';
        dctx.globalAlpha = alpha;
        dctx.lineJoin = 'round';
        dctx.lineWidth = 6;
        dctx.strokeStyle = 'rgba(28,20,12,0.9)';
        dctx.strokeText(pp.text, 0, 0);
        dctx.fillStyle = pp.color;
        dctx.fillText(pp.text, 0, 0);
        dctx.restore();
      }
      dctx.globalAlpha = 1;

      rafRef.current = requestAnimationFrame(frame);
    };
    rafRef.current = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDouble = (e: React.MouseEvent) => {
    const rect = dispRef.current!.getBoundingClientRect();
    dig(e.clientX - rect.left, e.clientY - rect.top);
  };

  // touch double-tap
  const lastTapRef = useRef({ t: 0, x: 0, y: 0 });
  const onTouchEnd = (e: React.TouchEvent) => {
    const touch = e.changedTouches[0];
    if (!touch) return;
    const rect = dispRef.current!.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;
    const now = performance.now();
    const lt = lastTapRef.current;
    if (now - lt.t < 320 && Math.hypot(x - lt.x, y - lt.y) < 40) {
      e.preventDefault();
      dig(x, y);
      lastTapRef.current = { t: 0, x, y };
    } else {
      lastTapRef.current = { t: now, x, y };
    }
  };

  const strengthLabel: Record<Strength, string> = {
    strong: '곡괭이 강타',
    medium: '탁, 한 삽',
    brush: '살살 붓질',
  };

  return (
    <div className="fossilDig">
      <ExperimentNav prompt={experiments.find((e) => e.id === 'fossil-dig')?.prompt} title="Web Fossil Dig" />

      <canvas
        ref={dispRef}
        className="fossilCanvas"
        onDoubleClick={onDouble}
        onTouchEnd={onTouchEnd}
      />

      <div className="fossilCounter">
        🦕 오늘의 발굴 깊이: <span className="fossilDepth">{String(depth).padStart(2, '0')}</span>층
        {depth >= D - 1 && <span className="fossilBedrock"> · 암반이다! 더는 못 파</span>}
      </div>

      {foundFossils.length > 0 && (
        <div className="fossilFinds">
          발굴한 화석&nbsp;
          {foundFossils.map((f) => (
            <span key={f} className="fossilFind">
              {f === 'bone' ? '🦴' : f === 'wifi' ? '📶' : f === 'floppy' ? '💾' : '🦖'}
            </span>
          ))}
        </div>
      )}

      <div className="fossilHint">
        ▸ 더블클릭으로 파세요 · 빠르게=곡괭이, 천천히=붓질 · 5층↓ 화석 등장
        {strength && <span className="fossilStrike"> — {strengthLabel[strength]}</span>}
      </div>
    </div>
  );
};

export default FossilDig;
